import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getAllGenres, addGenre, deleteGenre } from "../../services/GenreService";
import { ROLES } from "../../services/Constants";

const PageWrapper = styled.div`
    max-width: 600px;
    margin: 2rem auto;
    padding: 0 1rem;
`;

const PageTitle = styled.h2`
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 1.5rem;
    font-size: 1.6rem;
`;

const Card = styled.div`
    background: ${({ theme }) => theme.colors.card};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.md};
    padding: 1.5rem;
`;

const AddRow = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
`;

const TextInput = styled.input`
    flex: 1;
    padding: 0.55rem 0.75rem;
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.95rem;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    outline: none;
    &:focus {
        border-color: ${({ theme }) => theme.colors.primary};
    }
`;

const AddBtn = styled.button`
    padding: 0.55rem 1.1rem;
    background: ${({ theme }) => theme.colors.primary};
    color: #fff;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: 0.95rem;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    cursor: pointer;
    white-space: nowrap;
    &:hover { opacity: 0.9; }
    &:disabled { opacity: 0.5; cursor: default; }
`;

const GenreList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
`;

const GenreItem = styled.li`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.colors.background};
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    font-size: 0.95rem;
    color: ${({ theme }) => theme.colors.text};
`;

const DeleteBtn = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.error || "red"};
    cursor: pointer;
    font-size: 1rem;
    padding: 0 0.25rem;
    &:hover { opacity: 0.7; }
`;

const StatusMsg = styled.p`
    margin: 0.75rem 0 0;
    font-size: 0.9rem;
    color: ${({ $error, theme }) => $error ? (theme.colors.error || "red") : theme.colors.primary};
`;

const EmptyMsg = styled.p`
    color: ${({ theme }) => theme.colors.lineColor};
    font-style: italic;
    margin: 0;
`;

function GenreManagement({ user }) {
    const [genres, setGenres] = useState([]);
    const [newName, setNewName] = useState("");
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [statusMsg, setStatusMsg] = useState(null);
    const [statusError, setStatusError] = useState(false);

    const isAdmin = user?.role === ROLES.ADMIN;

    useEffect(() => {
        fetchGenres();
    }, []);

    async function fetchGenres() {
        try {
            const data = await getAllGenres();
            setGenres(data);
        } catch (err) {
            setStatusMsg("Greška pri dohvaćanju žanrova.");
            setStatusError(true);
        } finally {
            setLoading(false);
        }
    }

    async function handleAdd() {
        if (!newName.trim()) return;
        setAdding(true);
        setStatusMsg(null);
        try {
            const added = await addGenre(newName.trim());
            setGenres((prev) => [...prev, added]);
            setNewName("");
            setStatusMsg("Žanr uspješno dodan.");
            setStatusError(false);
        } catch (err) {
            setStatusMsg(err.message || "Greška pri dodavanju žanra.");
            setStatusError(true);
        } finally {
            setAdding(false);
        }
    }

    async function handleDelete(id) {
        setStatusMsg(null);
        try {
            await deleteGenre(id);
            setGenres((prev) => prev.filter((g) => g.id !== id));
            setStatusMsg("Žanr obrisan.");
            setStatusError(false);
        } catch (err) {
            setStatusMsg(err.message || "Greška pri brisanju žanra.");
            setStatusError(true);
        }
    }

    function handleKeyDown(e) {
        if (e.key === "Enter") handleAdd();
    }

    return (
        <PageWrapper>
            <PageTitle>Upravljanje žanrovima</PageTitle>
            <Card>
                <AddRow>
                    <TextInput
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Naziv novog žanra"
                        maxLength={100}
                    />
                    <AddBtn onClick={handleAdd} disabled={adding || !newName.trim()}>
                        Dodaj
                    </AddBtn>
                </AddRow>

                {loading ? (
                    <p>Učitavanje...</p>
                ) : genres.length === 0 ? (
                    <EmptyMsg>Nema unesenih žanrova.</EmptyMsg>
                ) : (
                    <GenreList>
                        {genres.map((g) => (
                            <GenreItem key={g.id}>
                                <span>{g.name}</span>
                                {isAdmin && (
                                    <DeleteBtn
                                        onClick={() => handleDelete(g.id)}
                                        title="Obriši žanr"
                                    >
                                        ✕
                                    </DeleteBtn>
                                )}
                            </GenreItem>
                        ))}
                    </GenreList>
                )}

                {statusMsg && (
                    <StatusMsg $error={statusError}>{statusMsg}</StatusMsg>
                )}
            </Card>
        </PageWrapper>
    );
}

export default GenreManagement;
